import { gql } from 'graphql-tag'

export const CHANNEL_FRAGMENT = gql`
  fragment Channel on channel {
    id
    world_id
    type
    title
    description
    world_level
  }
`

export const USER_ROLE_FRAGMENT = gql`
  fragment UserRole on user_role {
    role
  }
`

export const KEY_CLUB_CLUB_FRAGMENT = gql`
  fragment KeyClubClub on key_club_club {
    id
    name
    code
    key_club_district {
      id
      name
      code
    }
  }
`

export const USER_FRAGMENT = gql`
  fragment User on user {
    id
    display_name
    avatar
    pronouns
    username
    bio
    points
    is_verified
    timezone
    filtered_college_info
    key_club_club {
      ...KeyClubClub
    }
    user_roles {
      ...UserRole
    }
    member_since
  }

  ${USER_ROLE_FRAGMENT}
  ${KEY_CLUB_CLUB_FRAGMENT}
`

export const USER_ONLINE_FRAGMENT = gql`
  fragment UserOnline on user_online {
    id
    display_name
    avatar
    username
    pronouns
    bio
    last_online_at
    timezone
    user_roles {
      ...UserRole
    }
    key_club_district_name
    member_since
  }

  ${USER_ROLE_FRAGMENT}
`

export const WORLD_FRAGMENT = gql`
  fragment World on world {
    id
    slug
    title
    icon_url
    primary_color
  }
`

export const WORLD_MEMBER_FRAGMENT = gql`
  fragment WorldMember on world_member {
    id
    user_id
    world_id
    level
    goal
    snooze_goal_until
    details
    created_at
  }
`

export const LIVE_HELP_REQUEST_FRAGMENT = gql`
  fragment LiveHelpRequest on live_help_request {
    id
    post_id
    session_id
    user_id
    canceled_at
    completed_at
    created_at
  }
`

export const SESSION_REGISTRATION_FRAGMENT = gql`
  fragment SessionRegistration on session_registration {
    id
    user_id
    attempted_joining
  }
`

export const SESSION_HOST_FRAGMENT = gql`
  fragment SessionHost on session_host {
    id
    role
    user {
      ...User
    }
  }

  ${USER_FRAGMENT}
`

export const SERIES_HOST_FRAGMENT = gql`
  fragment SeriesHost on series_host {
    id
    role
    user {
      ...User
    }
  }

  ${USER_FRAGMENT}
`

export const SESSION_FRAGMENT = gql`
  fragment Session on session {
    id
    created_at
    title
    user_id
    start_date
    zoom_end_date
    description
    session_hosts {
      ...SessionHost
    }
    topic_id
    topic {
      name
    }
    world {
      ...World
    }
  }

  ${SESSION_HOST_FRAGMENT}
  ${WORLD_FRAGMENT}
`

export const SERIES_FRAGMENT = gql`
  fragment Series on series {
    id
    created_at
    title
    description
    user_id
    series_hosts {
      ...SeriesHost
    }
    world {
      ...World
    }
  }

  ${SERIES_HOST_FRAGMENT}
  ${WORLD_FRAGMENT}
`

export const POST_REACTION_FRAGMENT = gql`
  fragment PostReaction on post_reaction {
    id
    user {
      id
      display_name
    }
    reaction
    created_at
  }
`

export const POST_FOLLOW_FRAGMENT = gql`
  fragment PostFollow on post_follow {
    id
    post_id
    user_id
    created_at
  }
`

export const POST_EVENT_REACTION_FRAGMENT = gql`
  fragment PostEventReaction on post_event_reaction {
    id
    user {
      id
      display_name
    }
    reaction
    created_at
  }
`

export const NOTIFICATION_FRAGMENT = gql`
  fragment Notification on notification {
    id
    key
    user_id
    clicked_at
    notify_at
    payload
    user {
      ...User
    }
    related_user {
      ...User
    }
    secondary_related_user {
      ...User
    }
    world {
      ...World
    }
    user_event {
      id
      event_type
      event_payload
      event_points
    }
    course {
      id
      name
    }
    topic {
      id
      name
    }
    post {
      id
      channel {
        id
        title
        type
        world_level
      }
      user {
        ...User
      }
    }
    session {
      ...Session
    }
    series {
      ...Series
    }
    assessment {
      id
      title
    }
  }

  ${USER_FRAGMENT}
  ${SESSION_FRAGMENT}
  ${SERIES_FRAGMENT}
  ${WORLD_FRAGMENT}
`

export const CONVERSATION_USER_FRAGMENT = gql`
  fragment ConversationUser on conversation_user {
    id
    conversation_id
    user_id
    last_read_at
    archived_at
  }
`

export const MESSAGE_REACTION_FRAGMENT = gql`
  fragment MessageReaction on message_reaction {
    id
    user {
      id
      display_name
    }
    reaction
    created_at
  }
`

export const MESSAGE_FRAGMENT = gql`
  fragment Message on message {
    id
    conversation_id
    type
    body_safe
    attachment_urls_safe
    client_id
    user {
      ...User
    }
    last_edited_at
    message_reactions(order_by: { created_at: desc }) {
      ...MessageReaction
    }
    created_at
    updated_at
    hidden_at
  }

  ${USER_FRAGMENT}
  ${MESSAGE_REACTION_FRAGMENT}
`

export const POST_FEEDBACK_FRAGMENT = gql`
  fragment PostFeedback on post_feedback {
    id
    user {
      ...User
    }
    reviewer {
      ...User
    }
    post_id
    rating
    note
  }

  ${USER_FRAGMENT}
`

// Begin Post Fragments

export const POST_FRAGMENT = gql`
  fragment Post on post {
    id
    type
    body_safe
    status
    pinned_scope
    attachment_urls_safe
    created_at
    last_edited_at
    hidden_at
    world {
      slug
      title
      icon_url
      primary_color
    }
    payload
    user {
      ...User
    }
    channel {
      id
      title
      type
    }
    user_world_member {
      level
    }
    post_events_aggregate(where: { type: { _eq: "comment" } }) {
      aggregate {
        count
      }
    }
    live_help_requests(limit: 1, order_by: { created_at: desc }) {
      ...LiveHelpRequest
    }
    post_reactions(order_by: { created_at: desc }) {
      ...PostReaction
    }
    post_follows(where: { user_id: { _eq: $currentUserId } }) {
      ...PostFollow
    }
  }

  ${USER_FRAGMENT}
  ${LIVE_HELP_REQUEST_FRAGMENT}
  ${POST_REACTION_FRAGMENT}
  ${POST_FOLLOW_FRAGMENT}
`

export const POST_EVENT_FRAGMENT = gql`
  fragment PostEvent on post_event {
    id
    user_id
    post_id
    body_safe
    payload
    attachment_urls_safe
    is_collapsible
    client_id
    type
    created_at
    updated_at
    hidden_at
    user {
      ...User
      world_members(order_by: { created_at: desc }) {
        world_id
        level
      }
    }
    post {
      world {
        id
        slug
        title
        icon_url
        primary_color
      }
    }
    post_event_reactions {
      ...PostEventReaction
    }
  }

  ${USER_FRAGMENT}
  ${POST_EVENT_REACTION_FRAGMENT}
`
